// Simple i18n placeholder focused on Italian cart copy.
// In future, this can be replaced by a full locale injector from Liquid.

export type Locale = 'it';

type MessageCatalog = {
  cart: {
    addToCart: string;
    adding: string;
    added: string;
    addFailed: string;
    stockLimitExceeded: (maxQty: number) => string;
    cartIslandTitle: string;
    cartIslandDesc: string;
    totalLabel: string;
    freeShippingAchieved: string;
    freeShippingRemaining: (amount: string) => string;
    checkoutCta: (count: number) => string;
    widgetHiddenLabel: string;
    loading: string;
    emptyCart: string;
    startShopping: string;
    minOrder: (amount: string) => string;
    codUnavailable: (amount: string) => string;
    shippingAvailable: string;
    savings: (amount: string) => string;
    youMightLike: string;
    seeAll: string;
    // Discount Code Panel
    back: string;
    couponTitle: string;
    couponDesc: string;
    couponDescription: string;
    couponPlaceholder: string;
    applying: string;
    applyCoupon: string;
    couponApplied: string;
    couponInvalid: string;
    couponValidationFailed: string;
    couponCode: string;
    remove: string;
    howToUse: string;
    couponStep1: string;
    couponStep2: string;
    couponStep3: string;
    activeDiscounts: string;
    finalTotalNote: string;
    total: string;
    taxesIncluded: string;
    checkout: string;
    checkoutLocked: string;
    discount: string;
    viewCart: string;
    freeShippingUnlocked: string;
    cartTitle: string;
    openCart: string;
    emptyCartTitle: string;
    itemsUnit: string;
    stockLimitTitle: string;
    stockLimitDescRequested: (amount: string) => string;
    stockLimitDescDefault: (amount: string) => string;
    infoAvailable: string;
    cartSyncDegraded: string;
    cartSyncRetry: string;
    'cart.validation.required': string;
    'cart.validation.sellingPlanInvalid': string;
    'cart.validation.propertiesInvalid': string;
    'cart.validation.invalid': string;
    'cart.errors.itemNotFound': string;
    'cart.errors.stockLimit': (quantity: string) => string;
    'cart.errors.updateFailed': string;
    'cart.errors.addFailed': string;
    'cart.errors.couponInvalid': string;
    'cart.errors.couponValidationFailed': string;
    'cart.errors.generic': string;
    // Discount Summary
    totalSavings: string;
    couponCodeApplied: string;
    removeAllDiscounts: string;
    discountTitleFallback: string;
    discountDataInconsistency: string;
    // Validation & Reconciliation
    stockLimitAvailable: (amount: number | string) => string;
    validationRequired: string;
    validationSellingPlanInvalid: string;
    validationPropertiesInvalid: string;
    validationInvalid: string;
  };
};

const messages: Record<Locale, MessageCatalog> = {
  it: {
    cart: {
      addToCart: 'Aggiungi al carrello',
      adding: 'Aggiunta in corso…',
      added: 'Aggiunto al carrello',
      addFailed: 'Impossibile aggiungere al carrello, riprova più tardi',
      stockLimitExceeded: (maxQty: number) => `Quantità massima disponibile: ${maxQty}`,
      cartIslandTitle: 'Isola carrello',
      cartIslandDesc: 'Le funzioni del carrello saranno montate qui.',
      totalLabel: 'Totale',
      freeShippingAchieved: '🎉 Spedizione gratuita attiva',
      freeShippingRemaining: (amount: string) => `Ancora ${amount} per la spedizione gratuita`,
      checkoutCta: (count: number) => `Vai al checkout (${count})`,
      widgetHiddenLabel: 'Carrello vuoto',
      loading: 'Caricamento...',
      emptyCart: 'Il carrello è vuoto. Inizia a riempirlo!',
      startShopping: 'INIZIA LO SHOPPING',
      minOrder: (amount: string) => `Ordine richiesta minima: ${amount}`,
      codUnavailable: (amount: string) => `Il tuo ordine supera ${amount}. Il pagamento in contrassegno (COD) non è disponibile.`,
      shippingAvailable: 'Spedizione Standard e Contrassegno (COD) disponibili.',
      savings: (amount: string) => `Risparmi: ${amount}`,
      youMightLike: 'Potrebbe piacerti anche',
      seeAll: 'Vedi tutto',
      // Discount Code Panel
      back: 'Indietro',
      couponTitle: 'Codice sconto',
      couponDesc: 'Inserisci il codice sconto e clicca \'Applica\' per vedere lo sconto nel tuo totale finale.',
      couponDescription: 'Inserisci il tuo codice promozionale per ottenere uno sconto sul tuo ordine.',
      couponPlaceholder: 'Inserisci codice',
      applying: 'Applicazione...',
      applyCoupon: 'Applica codice',
      couponApplied: 'Codice sconto applicato!',
      couponInvalid: 'Codice sconto non valido',
      couponValidationFailed: 'Convalida del codice sconto non riuscita',
      couponCode: 'Codice',
      remove: 'Rimuovi',
      howToUse: 'Come funziona?',
      couponStep1: 'Inserisci il codice sconto nell\'apposito campo',
      couponStep2: 'Clicca su "Applica" per verificare la validità',
      couponStep3: 'Il sconto verrà applicato automaticamente al checkout',
      activeDiscounts: 'Sconti Attivi',
      finalTotalNote: 'Il totale finale sarà visibile al Checkout.',
      total: 'Totale(IVA incl.)',
      taxesIncluded: 'Spedizione calcolata al checkout.',
      checkout: 'Procedi al checkout',
      checkoutLocked: 'AGGIORNAMENTO CARRELLO...',
      discount: 'Sconto',
      viewCart: 'Visualizza Carrello',
      freeShippingUnlocked: 'Spedizione gratuita sbloccata!',
      cartTitle: 'Carrello',
      openCart: 'Apri Carrello',
      emptyCartTitle: 'Il tuo carrello è vuoto',
      itemsUnit: 'articoli',
      stockLimitTitle: 'Quantità massima disponibile',
      stockLimitDescRequested: (amount: string) => `Non puoi aggiungere più di ${amount} unità di questo prodotto.`,
      stockLimitDescDefault: (amount: string) => `Puoi aggiungere massimo ${amount} unità di questo prodotto.`,
      infoAvailable: 'Informazioni disponibili',
      cartSyncDegraded: 'Sincronizzazione carrello non riuscita. Puoi comunque procedere al checkout.',
      cartSyncRetry: 'Riprova',
      'cart.validation.required': 'Campo obbligatorio mancante',
      'cart.validation.sellingPlanInvalid': 'Piano di vendita non valido',
      'cart.validation.propertiesInvalid': 'Proprietà articolo non valide',
      'cart.validation.invalid': 'Impossibile aggiungere l\'articolo',
      'cart.errors.itemNotFound': 'Articolo non trovato nel carrello',
      'cart.errors.stockLimit': (quantity: string) => `Quantità insufficiente. Disponibilità corretta a ${quantity}`,
      'cart.errors.updateFailed': 'Impossibile aggiornare l\'articolo',
      'cart.errors.addFailed': 'Impossibile aggiungere l\'articolo al carrello',
      'cart.errors.couponInvalid': 'Codice sconto non valido',
      'cart.errors.couponValidationFailed': 'Convalida del codice sconto non riuscita',
      'cart.errors.generic': 'Si è verificato un errore',
      // Discount Summary
      totalSavings: 'Risparmi totale',
      couponCodeApplied: 'Codice sconto applicato',
      removeAllDiscounts: 'Rimuovi tutti gli sconti',
      discountTitleFallback: 'Sconto',
      discountDataInconsistency: 'Rilevata incoerenza nei dati di sconto',
      // Validation & Reconciliation (IT)
      stockLimitAvailable: (amount: number | string) => `Disponibile: ${amount} unità`,
      validationRequired: 'Campo obbligatorio mancante',
      validationSellingPlanInvalid: 'Piano di vendita non valido',
      validationPropertiesInvalid: 'Proprietà articolo non valide',
      validationInvalid: 'Impossibile aggiungere l\'articolo'
    }
  }
};

const locale: Locale = 'it';

/**
 * 简易翻译函数，按 key 读取购物车文案并支持插值函数。
 * @param key 文案键名
 * @param args 当目标文案为函数时需要的动态参数
 * @returns 已插值的字符串
 */
export function t<K extends keyof MessageCatalog['cart']>(
  key: K,
  ...args: MessageCatalog['cart'][K] extends (...args: any[]) => any
    ? Parameters<MessageCatalog['cart'][K]>
    : []
): string {
  const value = messages[locale].cart[key];
  if (typeof value === 'function') {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - runtime handles function
    return value(...args);
  }
  return value as string;
}

/**
 * 格式化金额（支持 cents 单位）
 * @param amountCents 金额（单位：cents/分）
 * @param currencyCode 货币代码
 * @returns 格式化后的货币字符串
 */
export function formatCurrency(amountCents: number, currencyCode = 'EUR') {
  try {
    // 将 cents 转换为主货币单位（欧元）
    const amount = amountCents / 100;
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  } catch {
    const amount = amountCents / 100;
    return `${amount.toFixed(2)} ${currencyCode}`;
  }
}
